import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Container,
  LinearProgress,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import color from "../../Constants/colors";
import LangSwitchButton from "../Common/LanguageButton";
import theme from "../../Constants/theme";

const images = [
  "/landingpage1.webp",
  "/landingpage2.webp",
  "/landingpage3.webp",
  "/landingpage4.webp",
  "/landingpage5.webp",
  "/landingpage6.webp",
];

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("home");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [leftHeight, setLeftHeight] = useState<number | null>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const services = Object.values(
    t("servicesSection.cards", { returnObjects: true })
  ) as { title: string; description: string; hover?: string }[];

  useEffect(() => {
    images.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    const auth = localStorage.getItem("cih_token");
    if (auth) {
      navigate("/home", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    setProgress(0);
    const duration = 6000;
    const step = 100 / (duration / 100);
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setActiveIndex((prevIndex) => (prevIndex + 1) % services.length);
          return 0;
        }
        return prev + step;
      });
    }, 100);
    return () => clearInterval(interval);
  }, [activeIndex, services.length]);

  useEffect(() => {
    if (leftRef.current) {
      setLeftHeight(leftRef.current.offsetHeight);
    }
  }, [services.length, activeIndex]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const fullHeight = document.body.scrollHeight;
      setShowScrollTop(scrollTop + windowHeight >= fullHeight - 90);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "hidden",
        fontFamily: "'Work Sans', sans-serif",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100vh",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <video
          key={window.location.pathname}
          autoPlay
          loop
          muted
          playsInline
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            top: 0,
            left: 0,
            zIndex: 1,
          }}
        >
          <source src="/landingpage.mp4" type="video/mp4" />
        </video>
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            top: 0,
            left: 0,
            background: `linear-gradient(135deg, ${color.primary_dark} 0%, ${color.secondary} 100%)`,
            opacity: 0.65,
            zIndex: 2,
          }}
        />
        <Box sx={{ position: "absolute", top: 20, left: 20, zIndex: 3 }}>
          <LangSwitchButton
            sx={{
              backgroundColor: color.primary_dark,
              color: color.white,
              borderColor: color.primary_dark,
              "&:hover": {
                backgroundColor: color.accent,
                borderColor: color.accent,
                color: color.primary_dark,
              },
            }}
          />
        </Box>
        <Box sx={{ position: "fixed", top: 20, right: 20, zIndex: 9999 }}>
          <Button
            onClick={() => navigate("/login")}
            sx={{
              backgroundColor: color.accent,
              color: color.white,
              fontWeight: "bold",
              borderRadius: "25px",
              px: 4,
              py: 1.5,
              textTransform: "none",
              boxShadow: "0 6px 16px rgba(0,0,0,0.3)",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: color.primary_dark,
                color: color.accent,
                transform: "translateY(-2px)",
              },
              opacity: 0,
              animation: "fadeIn 1s 1.2s forwards",
            }}
          >
            {t("landing.login")}
          </Button>
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            color: color.white,
            px: 2,
            zIndex: 3,
            width: isMobile ? "90%" : "auto",
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontWeight: "bold",
              mb: 2,
              textShadow: "2px 2px 8px rgba(0,0,0,0.4)",
              opacity: 0,
              animation: "fadeInUp 1s 0.5s forwards",
            }}
          >
            {t("landing.title")}
          </Typography>
          <Typography
            variant={isMobile ? "body2" : "body1"}
            sx={{ marginBottom: 2, animation: "fadeInUp 1s 1s forwards" }}
          >
            {t("landing.text")}
          </Typography>
          <Button
            onClick={() =>
              window.open(
                "https://corporatemomentum.zohobookings.eu/#/109854000000018042",
                "_blank"
              )
            }
            sx={{
              backgroundColor: color.accent,
              color: color.white,
              px: 6,
              py: 1.8,
              borderRadius: "30px",
              fontWeight: "bold",
              textTransform: "none",
              boxShadow: "0 6px 18px rgba(0,0,0,0.3)",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: color.accent_dark,
                transform: "translateY(-3px)",
              },
              opacity: 0,
              animation: "fadeInUp 1s 1.5s forwards",
            }}
          >
            {t("landing.getStarted")}
          </Button>
        </Box>
        {!isMobile && (
          <Box sx={{ position: "fixed", left: 20, bottom: 20, zIndex: 3 }}>
            <img
              src="/logo_big.png"
              alt="Logo"
              style={{
                maxWidth: "200px",
                maxHeight: "200px",
                opacity: 0,
                animation: "fadeIn 1s 1.5s forwards",
              }}
            />
          </Box>
        )}
        {!isMobile && (
          <Box
            sx={{
              position: "absolute",
              bottom: 40,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box className="mouse" />
          </Box>
        )}
      </Box>
      <Box sx={{ width: "100%", py: 10, backgroundColor: color.primary_dark }}>
        <Container
          maxWidth={false}
          sx={{ width: isMobile ? "95%" : "70%", mx: "auto" }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              mb: 6,
            }}
          >
            {t("servicesSection.title")}
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box
                ref={leftRef}
                sx={{ display: "flex", flexDirection: "column", gap: 3 }}
              >
                {services.map((service, index) => (
                  <Box
                    key={index}
                    onClick={() => {
                      setActiveIndex(index);
                      setProgress(0);
                    }}
                    sx={{
                      cursor: "pointer",
                      p: 2,
                      borderLeft:
                        index === activeIndex
                          ? `4px solid ${color.accent}`
                          : "4px solid transparent",
                      backgroundColor:
                        index === activeIndex
                          ? "rgba(242,141,1,0.1)"
                          : "transparent",
                      position: "relative",
                      borderRadius: 2,
                      overflow: "hidden",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {isMobile && index === activeIndex && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          backgroundImage: `url(${
                            images[activeIndex % images.length]
                          })`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          filter: "brightness(0.5)",
                          zIndex: 0,
                        }}
                      />
                    )}
                    <Box sx={{ position: "relative", zIndex: 1 }}>
                      {index === activeIndex && (
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{
                            height: 4,
                            borderRadius: 2,
                            mb: 1,
                            backgroundColor: "rgba(255,255,255,0.1)",
                            "& .MuiLinearProgress-bar": {
                              backgroundColor: color.accent,
                            },
                          }}
                        />
                      )}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: index === activeIndex ? "bold" : "normal",
                          color:
                            index === activeIndex ? color.accent : color.white,
                        }}
                      >
                        {service.title}
                      </Typography>
                      {index === activeIndex && (
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.5,
                            opacity: 0,
                            transform: "translateY(20px)",
                            animation: "fadeSlideIn 0.5s forwards",
                            animationDelay: "0.2s",
                          }}
                        >
                          {service.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
            {!isMobile && (
              <Grid item xs={12} md={8}>
                <Box
                  sx={{
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                    height: leftHeight || 400,
                    transition: "height 0.3s ease",
                  }}
                >
                  <Box
                    component="img"
                    key={activeIndex}
                    src={images[activeIndex % images.length]}
                    alt={services[activeIndex].title}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      opacity: 0,
                      animation: "fadeInImage 0.8s forwards",
                    }}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>
      <Box sx={{ width: "100%", py: 10, backgroundColor: color.primary_dark }}>
        <Container
          maxWidth={false}
          sx={{ width: isMobile ? "95%" : "70%", mx: "auto" }}
        >
          <Typography
            variant="h6"
            sx={{
              color: color.accent,
              fontWeight: "bold",
              textAlign: "center",
              textTransform: "uppercase",
            }}
          >
            {t("landing.faqSubtitle")}
          </Typography>

          <Typography
            variant="h3"
            sx={{
              mb: 6,
              lineHeight: 1.2,
            }}
          >
            {t("landing.faqTitle")}
          </Typography>

          {(
            t("faq", { returnObjects: true }) as {
              question: string;
              answer: string;
            }[]
          ).map((item, index) => (
            <Accordion
              key={index}
              disableGutters
              sx={{
                mb: 2,
                borderRadius: 3,
                overflow: "hidden",
                backgroundColor: "rgba(255,255,255,0.05)",
                backdropFilter: "blur(10px)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                transition: "all 0.3s ease",
                "&:hover": { transform: "translateY(-2px)" },
                "& .MuiAccordionSummary-root": { padding: 2 },
                "& .MuiAccordionDetails-root": { px: 3, pt: 0, pb: 1 },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{
                      color: color.white,
                      fontSize: "2rem",
                      transition: "transform 0.3s ease",
                      "&.Mui-expanded": {
                        transform: "rotate(180deg)",
                      },
                    }}
                  />
                }
                sx={{
                  "& .MuiAccordionSummary-content": {
                    alignItems: "center",
                    margin: 0,
                  },
                }}
              >
                <Typography variant="h5" sx={{ color: color.white }}>
                  {item.question}
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                <Typography
                  variant="body1"
                  sx={{
                    color: color.primary_not_present_in_design,
                    lineHeight: 1.7,
                    animation: "fadeSlideIn 0.5s forwards",
                  }}
                >
                  {item.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>
      <Box
        sx={{
          width: "100%",
          py: isMobile ? 1 : 10,
          backgroundColor: color.primary_dark,
        }}
      >
        <Container
          maxWidth={false}
          sx={{ width: isMobile ? "95%" : "70%", mx: "auto" }}
        >
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: color.secondary,
                  backgroundImage: `url(/background.png)`,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                  borderRadius: 3,
                  overflow: "hidden",
                  boxShadow: "0 10px 24px rgba(0,0,0,0.4)",
                  minHeight: 300,
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  p: 5,
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: "bold", mb: 2 }}>
                  {t("landing.startJourney")}
                </Typography>
                <Typography variant="h6" sx={{ mb: 4, color: color.accent }}>
                  {t("landing.joinUs")}
                </Typography>
                <Button
                  onClick={() =>
                    window.open(
                      "https://corporatemomentum.zohobookings.eu/#/109854000000018042",
                      "_blank"
                    )
                  }
                  sx={{
                    backgroundColor: color.accent,
                    color: color.white,
                    px: 5,
                    py: 1.5,
                    borderRadius: "30px",
                    fontWeight: "bold",
                    textTransform: "none",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: color.accent_dark,
                      transform: "translateY(-3px)",
                    },
                  }}
                >
                  {t("landing.getStarted")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Box
        sx={{
          py: 4,
          px: 2,
          backgroundColor: color.primary_dark,
          color: color.white,
          textAlign: "center",
          fontFamily: "'Work Sans', sans-serif",
        }}
      >
        <Typography
          variant="body2"
          sx={{ opacity: 0.8, mb: 1, fontSize: "0.95rem" }}
        >
          {t("footer.rights", { year: new Date().getFullYear() })}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: color.accent, transition: "all 0.3s ease" }}
        >
          {t("footer.contact")}
        </Typography>
      </Box>
      {showScrollTop && !isMobile && (
        <Button
          onClick={scrollToTop}
          sx={{
            position: "fixed",
            right: 100,
            bottom: 100,
            zIndex: 999,
            backgroundColor: color.accent,
            color: color.white,
            minWidth: "auto",
            borderRadius: "50%",
            width: 56,
            height: 56,
            fontSize: "1.2rem",
            boxShadow: "0 6px 16px rgba(0,0,0,0.4)",
            transition: "all 0.3s ease",
            animation: "mediumBounce 0.5s",
            "@keyframes mediumBounce": {
              "0%": { transform: "scale(0.8)", opacity: 0 },
              "60%": { transform: "scale(1.1)", opacity: 1 },
              "80%": { transform: "scale(0.97)" },
              "100%": { transform: "scale(1)" },
            },
            "&:hover": {
              backgroundColor: color.accent_dark,
              transform: "scale(1.1)",
            },
          }}
        >
          <ArrowUpwardIcon />
        </Button>
      )}
      <style>
        {`
          @keyframes fadeInUp { 
            0% { opacity:0; transform: translateY(30px); } 
            100% { opacity:1; transform: translateY(0); } 
          }
          @keyframes fadeIn { 
            0% { opacity:0; } 
            100% { opacity:1; } 
          }
          @keyframes fadeSlideIn {
            0% { opacity:0; transform: translateY(20px); }
            100% { opacity:1; transform: translateY(0); }
          }
          @keyframes fadeInImage {
            0% { opacity:0; }
            100% { opacity:1; }
          }
          .mouse { 
            width:30px; height:50px; border:2px solid white; border-radius:20px; position:relative; 
          }
          .mouse::before { 
            content:""; width:6px; height:6px; background:white; position:absolute; top:10px; 
            left:50%; transform:translateX(-50%); border-radius:50%; animation:wheel 1.5s infinite; 
          }
          @keyframes wheel { 
            0% { top:10px; opacity:1; } 
            100% { top:30px; opacity:0; } 
          }
        `}
      </style>
    </Box>
  );
};

export default LandingPage;
